import React, { useMemo } from "react"
import { add } from "dinero.js"
import { makeStyles } from "tss-react/mui"
import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material"

import { calculateTotalAmountFromDeliveries } from "../../../createOrder/components/drawer/orderSummary/helpers"
import { DeliveryTimesField } from "../../../createOrder/components/form/formTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { getExtraServiceBreakdown } from "../../../createOrder/components/drawer/extraServices/extraServiceBreakdown"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { toDinero } from "../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../modules/currency/useMoney"
import { calculateVATDinero } from "./helpers"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import { selectEffectiveUserType } from "../../../../../modules/context/contextSelectors"
import { UserType } from "../../../../../modules/login/loginTypes"
import {
    BlockTitle,
    OrderAmount,
    SectionItemData,
    SectionItemLabel,
    SectionSubTitle,
    SectionTitle,
    TableColumnLabel,
} from "./common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    title: {
        padding: "0 1.25rem",
        "@media print": {
            display: "none",
        },
    },
    tableWrap: {
        borderRadius: 0,
        overflow: "hidden",
        boxShadow: "none",
        "& thead .MuiTableRow-root": {
            backgroundColor: tailwindPalette.slate[100],
        },
    },
    cell: {
        fontSize: "1rem",
        border: "none",
        padding: ".25rem 1rem",
    },
    cellTitle: {
        padding: "0.75rem 1.25rem 0",
    },
    cellPadding: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: "2.25rem",
    },
    divider: {
        padding: "0",
        paddingTop: "1rem",
    },
    headCell: {
        padding: "0.75rem 1.25rem",
        borderBottom: "none",
    },
    footer: {
        color: theme.palette.text.primary,
        "@media print": {
            td: {
                paddingBottom: 0,
                paddingTop: 0,
            },
        },
        "& .MuiTableCell-root": {
            borderBottom: "none",
        },
    },
    orderLineRow: {
        "& .MuiTableCell-root": {
            paddingTop: 0,
            paddingBottom: 0,
        },
    },
    greyColor: {
        color: tailwindPalette.gray[500],
    },
    footerTopRow: {
        display: "grid",
        gridTemplateRows: "3rem 1fr",
        gap: ".5rem",
        alignItems: "center",
        textAlign: "right",
    },
    deliveryBlock: {
        padding: "0 1.25rem",
        paddingBottom: theme.spacing(2),
        gap: "0.5rem",
        [theme.breakpoints.down("sm")]: {
            paddingTop: "0.25rem",
        },
    },
    rowItem: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "1rem",
    },
    orderSummaryContainer: {
        [theme.breakpoints.down("sm")]: {
            borderBottom: `1px solid ${theme.palette.divider}`,
            paddingBottom: "2rem",
        },
    },
    orderSummaryTitle: {
        display: "block",
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    meetingServiceTitle: {
        display: "none",
        [theme.breakpoints.down("sm")]: {
            display: "block",
            padding: "0.625rem 1.25rem",
        },
    },
    table: {
        borderCollapse: "separate",
        borderSpacing: "0 0.5rem",
    },
}))

const VAT_PERCENTAGE = 25

type MoneyFormatter = { format: (value: ReturnType<typeof toDinero>) => string }

function renderExtraServiceBreakdown(
    line: {
        isExtraService?: boolean
        extraServicePricePerSeat?: Parameters<typeof toDinero>[0]
        extraServiceMinPrice?: Parameters<typeof toDinero>[0]
        extraServiceMaxPrice?: Parameters<typeof toDinero>[0]
    },
    seats: number,
    moneyFactory: MoneyFormatter,
): string | null {
    if (!line.isExtraService) return null
    const bd = getExtraServiceBreakdown(
        seats,
        line.extraServicePricePerSeat,
        line.extraServiceMinPrice,
        line.extraServiceMaxPrice,
    )
    if (bd?.kind === "seats") return `${seats} seats x ${moneyFactory.format(toDinero(bd.pricePerSeat))}`
    if (bd?.kind === "min") return `Min. price ${moneyFactory.format(toDinero(bd.minPrice))}`
    if (bd?.kind === "max") return `Max. price ${moneyFactory.format(toDinero(bd.maxPrice))}`
    return null
}

type OrderSummaryProps = Readonly<{
    deliveryDetails: DeliveryTimesField
    isRefundOrder: boolean
    priceListIncludeVat: boolean
    numberOfSeats?: number
}>

export function OrderSummary({
    deliveryDetails,
    isRefundOrder,
    priceListIncludeVat,
    numberOfSeats = 0,
}: OrderSummaryProps) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const userType = useAppSelector(selectEffectiveUserType)
    const isMerchant = userType === UserType.MERCHANT

    const totalAmount = useMemo(
        () => calculateTotalAmountFromDeliveries(deliveryDetails.deliveryTimes),
        [deliveryDetails.deliveryTimes],
    )
    const vatAmount = useMemo(() => calculateVATDinero(totalAmount, VAT_PERCENTAGE), [totalAmount])
    const totalAmountInclVat = useMemo(() => add(totalAmount, vatAmount), [totalAmount, vatAmount])

    const formattedDate = deliveryDetails.deliveryDate
        ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(deliveryDetails.deliveryDate))
        : undefined

    const renderDeliverySubTitle = (deliveryTime: string | undefined | null) => {
        if (!formattedDate) return null

        const formattedTime = deliveryTime
            ? dateTimeFactory.formatTime(dateTimeFactory.fromHourMinute(deliveryTime))
            : undefined

        if (!formattedTime) {
            return (
                <SectionSubTitle
                    translationKey="confirm-order-dialog-order-summary-delivery-date"
                    translationVars={{ date: formattedDate }}
                >
                    Delivery: {formattedDate}
                </SectionSubTitle>
            )
        }

        return (
            <SectionSubTitle
                translationKey="confirm-order-dialog-order-summary-delivery-date-time"
                translationVars={{ date: formattedDate, time: formattedTime }}
            >
                Delivery: {formattedDate}, {formattedTime}
            </SectionSubTitle>
        )
    }

    const footerTableRowStack = (
        <Stack flexDirection="row" gap="1.5rem" justifyContent="flex-end" alignItems="center">
            <Box className={classes.footerTopRow}>
                <SectionItemData
                    translationKey={
                        isMerchant
                            ? "confirm-order-dialog-order-summary-table-meeting-services-total"
                            : "confirm-order-dialog-order-summary-table-meeting-services-total-no-vat"
                    }
                >
                    Total
                </SectionItemData>
                <SectionItemLabel
                    translationKey="confirm-order-dialog-order-summary-table-meeting-services-vat"
                    translationVars={{ percentage: VAT_PERCENTAGE }}
                >
                    VAT ({VAT_PERCENTAGE}%)
                </SectionItemLabel>
                {!priceListIncludeVat && (
                    <SectionItemLabel translationKey="confirm-order-dialog-order-summary-table-meeting-services-total-include-vat">
                        Total amount incl. VAT
                    </SectionItemLabel>
                )}
            </Box>
            <Stack className={classes.footerTopRow}>
                <OrderAmount>
                    {isRefundOrder && "-"}
                    {moneyFactory.format(totalAmount)}
                </OrderAmount>
                <SectionItemLabel>
                    {isRefundOrder && "-"}
                    {moneyFactory.format(vatAmount)}
                </SectionItemLabel>
                {!priceListIncludeVat && (
                    <SectionItemLabel>
                        {isRefundOrder && "-"}
                        {moneyFactory.format(totalAmountInclVat)}
                    </SectionItemLabel>
                )}
            </Stack>
        </Stack>
    )

    if (isMobile) {
        return (
            <Stack gap=".75rem" className={classes.orderSummaryContainer}>
                <BlockTitle translationKey="confirm-order-dialog-order-summary" classNames={classes.orderSummaryTitle}>
                    Order summary
                </BlockTitle>
                <SectionTitle
                    translationKey="confirm-order-dialog-order-summary-table-meeting-service"
                    classNames={classes.meetingServiceTitle}
                >
                    Meeting service
                </SectionTitle>
                {deliveryDetails.deliveryTimes.map((delivery, index) => {
                    return (
                        <Stack key={index} className={classes.deliveryBlock}>
                            {renderDeliverySubTitle(delivery.time)}

                            <Stack gap={1}>
                                {delivery.orderLines.map((line) => {
                                    const totalPrice = line.price
                                        ? toDinero({
                                              amount: line.price.amount * line.quantity,
                                              scale: line.price.scale,
                                              currency: line.price.currency,
                                          })
                                        : null

                                    const breakdownLabel = renderExtraServiceBreakdown(
                                        line,
                                        numberOfSeats,
                                        moneyFactory,
                                    )

                                    return (
                                        <Stack key={line.id} gap=".125rem">
                                            <Box className={classes.rowItem}>
                                                <SectionItemData>
                                                    {line.isExtraService
                                                        ? line.name
                                                        : `${line.quantity} x ${line.name}`}
                                                </SectionItemData>
                                                <SectionItemData>
                                                    {isRefundOrder && "-"}
                                                    {totalPrice && moneyFactory.format(totalPrice)}
                                                </SectionItemData>
                                            </Box>
                                            {breakdownLabel && (
                                                <Box className={classes.rowItem}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {breakdownLabel}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    )
                                })}
                            </Stack>
                        </Stack>
                    )
                })}
                {footerTableRowStack}
            </Stack>
        )
    }

    return (
        <Stack gap="2rem">
            <BlockTitle translationKey="confirm-order-dialog-order-summary">Order summary</BlockTitle>
            <TableContainer component={Paper} className={classes.tableWrap}>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableColumnLabel
                                classNames={classes.headCell}
                                translationKey="confirm-order-dialog-order-summary-table-meeting-service"
                            >
                                Meeting service
                            </TableColumnLabel>
                            <TableColumnLabel
                                classNames={classes.headCell}
                                translationKey="confirm-order-dialog-order-summary-table-items"
                                align="right"
                            >
                                Items
                            </TableColumnLabel>
                            <TableColumnLabel
                                classNames={classes.headCell}
                                translationKey={
                                    isMerchant
                                        ? "confirm-order-dialog-order-summary-table-amount"
                                        : "confirm-order-dialog-order-summary-table-amount-no-vat"
                                }
                                align="right"
                            >
                                Amount
                            </TableColumnLabel>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {deliveryDetails.deliveryTimes.map((delivery, index) => (
                            <React.Fragment key={index}>
                                <TableRow>
                                    <TableCell colSpan={3} className={cx(classes.cell, classes.cellTitle)}>
                                        {renderDeliverySubTitle(delivery.time)}
                                    </TableCell>
                                </TableRow>
                                {delivery.orderLines.map((line, idx) => {
                                    const { price, quantity, name, isExtraService } = line
                                    const totalPrice = price
                                        ? toDinero({
                                              amount: price.amount * quantity,
                                              scale: price.scale,
                                              currency: price.currency,
                                          })
                                        : null
                                    const breakdownLabel = renderExtraServiceBreakdown(
                                        line,
                                        numberOfSeats,
                                        moneyFactory,
                                    )

                                    return (
                                        <TableRow key={idx} className={classes.orderLineRow}>
                                            <TableCell className={cx(classes.cell, classes.cellPadding)}>
                                                <SectionItemData>{name}</SectionItemData>
                                            </TableCell>
                                            <TableCell align="right" className={classes.cell}>
                                                {isExtraService && breakdownLabel ? (
                                                    <SectionItemData>{breakdownLabel}</SectionItemData>
                                                ) : (
                                                    <SectionItemData>
                                                        {quantity}{" "}
                                                        {price && `x ${moneyFactory.format(toDinero(price))}`}
                                                    </SectionItemData>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" className={classes.cell}>
                                                <SectionItemData>
                                                    {totalPrice && (
                                                        <>
                                                            {isRefundOrder && "-"}
                                                            {moneyFactory.format(totalPrice)}
                                                        </>
                                                    )}
                                                </SectionItemData>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                <TableRow>
                                    <TableCell colSpan={3} className={classes.divider}></TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                        <TableRow className={classes.footer}>
                            <TableCell colSpan={3}>{footerTableRowStack}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    )
}
